import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IcdSuggestionService } from 'src/app/core/services/icd-suggestion.service';
import { buildTimeline as buildTimelinePure, categoryLabel as categoryLabelPure } from './clinical-summary-timeline';

/**
 * The patient's standing clinical context, in point form (§11.14).
 *
 * What it is: the conclusions previous coding reviews drew from this patient's timeline, carried
 * forward so the next review — and a clinician opening the file — starts with the background
 * rather than re-reading everything.
 *
 * ⚠ WHY THIS SCREEN SAYS SO LOUDLY THAT IT IS AI-GENERATED
 *
 * Every line here is model-authored prose about clinical text. It is NOT a quote, it is NOT part
 * of the record, and it can be wrong. The pipeline itself is forbidden from using any of it as
 * evidence for a code — `scripts/icd-summary-check.js` pins that — but a clinician reading a
 * tidy list of clinical facts will read it as record unless told otherwise. The banner and the
 * generated-on date are the mitigation, and they are not decoration.
 *
 * Deliberately its own component rather than a third mode on the suggestions dialog: it shares
 * nothing with that screen — no accept, no reject, no evidence, no audit entries — and bolting
 * it on would make a component that already has two modes into one with three.
 */
@Component({
  selector: 'app-clinical-summary-dialog',
  templateUrl: './clinical-summary-dialog.component.html',
  styleUrls: ['./clinical-summary-dialog.component.scss']
})
export class ClinicalSummaryDialogComponent implements OnInit {

  patientId: string;
  patientName: string;

  points: any[] = [];
  generatedAt: string | null = null;
  loading = true;
  loadError: string | null = null;

  /**
   * The points grouped into a timeline: one entry per date, newest first.
   *
   * Replaced a grouping by category, which read badly for the thing this actually is. That
   * version repeated the same date on seven consecutive lines, ordered them arbitrarily within
   * each category (22 Jul, 22 Jul, 13 Jun, 13 Jun, 22 Jul, 5 May), and split a single day's
   * events across two headings — so the resection and the findings it produced sat in different
   * sections. A clinician opening this wants "what happened, most recent first", not an index.
   */
  timeline: Array<{ key: string; label: string; points: any[] }> = [];

  constructor(
    private icdSuggestionService: IcdSuggestionService,
    private dialogRef: MatDialogRef<ClinicalSummaryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.patientId = data?.patientId;
    this.patientName = data?.patientName || 'this patient';
  }

  ngOnInit(): void {
    this.icdSuggestionService.getClinicalSummary(this.patientId).subscribe(
      (response) => {
        const summary = response?.summary;
        this.points = summary?.points || [];
        this.timeline = this.buildTimeline(this.points);
        this.generatedAt = summary?.generatedAt || null;
        this.loading = false;
      },
      (error) => {
        console.error('Error loading the clinical summary:', error);
        this.loadError = 'The summary could not be loaded. Please try again.';
        this.loading = false;
      }
    );
  }

  // Grouping lives in ./clinical-summary-timeline — pure, Angular-free, and checked. It is the
  // whole readability of this screen, so it is worth exercising directly rather than by eye.
  private buildTimeline(points: any[]): Array<{ key: string; label: string; points: any[] }> {
    return buildTimelinePure(points);
  }

  categoryLabel(category: string): string {
    return categoryLabelPure(category);
  }

  close(): void {
    this.dialogRef.close();
  }
}
